import { Box, Button, Flex, IconButton, Stack, Text, Wrap } from '@chakra-ui/react'
import { faEllipsis, faFlag, faSmilePlus } from '@fortawesome/pro-regular-svg-icons'
import { faEdit, faTrash } from '@fortawesome/pro-solid-svg-icons'
import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { SHWError } from '@/common/error'
import { isNotNull } from '@/utils/helpers'

import { useUser } from '@/client/context/user'
import { useServerNow } from '@/client/hooks'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import { CONVERSATION_EDIT_ALLOWED_MINUTES, type MessageType } from '@/client/services/conversation'
import { getMessageRichText } from '@/client/services/conversation/getMessageRichText'
import type { MessagePlainV1MessageFragment } from '@/client/services/hasura/generated/graphql'
import getBodyForHiddenEntity from '@/client/services/hidden/getBodyForHiddenEntity'
import type { SerializedRichText } from '@/client/services/richText/types'
import { getAdminUrl } from '@/client/services/url'
import { hasUserRole } from '@/client/services/user'
import hasUserAuthScope from '@/client/services/user/hasUserAuthScope'

import ReportModalV2 from '@/components/Report/ReportModalV2'
import ShwLink from '@/components/ShwLink'
import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import Icon from '@/components/UI/Icon/Icon'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import RelativeTime from '@/components/UI/RelativeTime/RelativeTime'
import RichText from '@/components/UI/RichTextEditor/RichText'
import FileUploadGallery from '@/components/Worlds/FileUpload/FileUploadGallery'
import { getProcessedReactions, getReactionListWithNewReaction } from '@/components/Worlds/helpers'
import { MAX_REACTIONS_TO_SHOW } from '@/components/Worlds/Reaction/constants'
import ReactionButton from '@/components/Worlds/Reaction/ReactionButton'
import ReactionWrapItems from '@/components/Worlds/Reaction/ReactionWrapItems'
import type { ReactionDetails } from '@/components/Worlds/types'
import Avatar from '@/components/Worlds/UI/Avatar/Avatar'
import Menu from '@/components/Worlds/UI/Menu/Menu'
import type { MenuItemType } from '@/components/Worlds/UI/Menu/types'

import { EMPTY_MESSAGE_ID } from './constants'

const HIDE_SEPARATOR_THRESHOLD_MINUTES = 10

export default function MessagePlainV1({
  message,
  lastMessage,
  isAdminView = false,
}: {
  message: MessagePlainV1MessageFragment
  lastMessage: MessagePlainV1MessageFragment | null
  isAdminView?: boolean
}): React.ReactElement {
  const user = useUser()
  const router = useRouter()
  const now = useServerNow()
  const { ref, inView } = useInView()

  const [activeModal, setActiveModal] = useState<'edit' | 'report' | 'delete' | null>(null)
  const [showAllReactions, setShowAllReactions] = useState(false)
  const [optionsVisible, setOptionsVisible] = useState(false)

  const messageUserId = message.user?.id
  const messageBody = getMessageRichText({
    type: message.type as MessageType,
    body: message.body_safe,
  })

  const isCurrentUser = message.user == null ? false : messageUserId === user.id
  const isDeletedMessage = message.type === 'deleted-user-v1'
  const isHiddenMessage = message.hidden_at != null && !isDeletedMessage
  const isRemovedMessage = isHiddenMessage || isDeletedMessage

  const canEditMessage =
    (isAdminView && hasUserAuthScope(user, 'admin:messages:modify')) ||
    (isCurrentUser &&
      message.id > 0 &&
      DateTime.fromISO(message.created_at) >
        now.minus({ minutes: CONVERSATION_EDIT_ALLOWED_MINUTES }))

  const canReportMessage = !isCurrentUser && !isRemovedMessage
  const canDeleteMessage =
    ((isAdminView && hasUserAuthScope(user, 'admin:messages:modify')) || isCurrentUser) &&
    message.id > 0 &&
    !isRemovedMessage

  const menuItems: MenuItemType[] = [
    canReportMessage
      ? { icon: faFlag, label: 'Report Message', onClick: () => setActiveModal('report') }
      : null,
    canEditMessage
      ? { icon: faEdit, label: 'Edit Message', onClick: () => setActiveModal('edit') }
      : null,
    canDeleteMessage
      ? { icon: faTrash, label: 'Delete Message', onClick: () => setActiveModal('delete') }
      : null,
  ].filter(isNotNull)

  const richTextStyleProps = useMemo(
    () => ({
      color:
        message.id === EMPTY_MESSAGE_ID ? 'blue.muted' : isCurrentUser ? 'blue.contrast' : 'fg',
    }),
    [isCurrentUser, message.id]
  )

  const hideSeparator =
    lastMessage == null
      ? false
      : DateTime.fromISO(message.created_at) <
          DateTime.fromISO(lastMessage.created_at).plus({
            minutes: HIDE_SEPARATOR_THRESHOLD_MINUTES,
          }) && message.user?.id === lastMessage.user?.id
  const userDisplayName = message.user?.virtual_display_name ?? ''
  const messageDisplayName = isCurrentUser ? 'You' : userDisplayName
  const editMessageFormInputs: FormInputType[] = React.useMemo(
    () => [
      {
        type: 'rich-text',
        label: 'Content',
        name: 'content',
        required: true,
        initialValue: message.body_safe,
        size: 6,
      },
    ],
    [message.body_safe]
  )

  // Transform to local type because it's too much work just translating all the downstream types
  const reactions = useMemo(
    () =>
      message.message_reactions
        .filter(item => item.user != null)
        // ^ Exclude reactions from deleted users
        .map(item => ({
          reaction: item.reaction,
          createdAt: DateTime.fromISO(item.created_at),
          user: {
            id: item.user.id,
            displayName: item.user.virtual_display_name ?? '',
          },
        })),
    [message.message_reactions]
  )

  // Keep a local state for optimistic updates:
  const [currentReactions, setCurrentReactions] = useState<
    { reaction: string; reactionUsers: ReactionDetails[] }[]
  >(() => getProcessedReactions({ reactions }))

  // If the subscribed value changes:
  useEffect(() => {
    if (inView) {
      setCurrentReactions(getProcessedReactions({ reactions }))
    }
  }, [inView, reactions])

  const [onToggleReaction] = useAPIWrapper(
    async (newReaction: string) => {
      // Optimistic update
      const oldReactions = [...currentReactions]

      const newReactions = getReactionListWithNewReaction({
        reactions: currentReactions,
        newReaction,
        userId: user.id,
        userDisplayName: user.displayName,
      })

      setCurrentReactions(newReactions)

      if (
        oldReactions.length <= MAX_REACTIONS_TO_SHOW &&
        newReactions.length > MAX_REACTIONS_TO_SHOW
      ) {
        // If we pass the threshold, expand all reactions to reduce potential confusion
        setShowAllReactions(true)
      }

      try {
        await RPC.toggleMessageReaction(
          {
            messageId: message.id,
            reaction: newReaction,
          },
          {
            disableProgressBar: true,
          }
        )
      } catch (err) {
        // Revert optimistic update
        setCurrentReactions(oldReactions)
        throw err
      }
    },
    [message.id, user.id, currentReactions, user.displayName],
    'Toggle Message Reaction'
  )

  const handleSubmitEditMessage = useCallback(
    async (values: FormValues) => {
      const editMessageResult = await RPC.editConversationMessage({
        messageId: message.id,
        newBody: values.content as SerializedRichText,
      })

      if (editMessageResult.status === 'blocked') {
        throw new SHWError({
          externalMessage: editMessageResult.message,
        })
      }

      if (editMessageResult.status === 'maybe-suspended') {
        router.reload()
        return
      }

      setActiveModal(null)
    },
    [message.id, router]
  )

  const handleSubmitDeleteMessage = useCallback(async () => {
    await RPC.deleteConversationMessage({
      messageId: message.id,
    })

    setActiveModal(null)
  }, [message.id])

  return (
    <Stack direction={isCurrentUser ? 'row-reverse' : 'row'} spacing="16px" role="group" ref={ref}>
      {isCurrentUser ? null : (
        <Stack width="32px" direction="column-reverse">
          {hideSeparator || message.user == null ? null : <Avatar user={message.user} size="sm" />}
        </Stack>
      )}
      <Flex
        maxWidth={{ base: '95%', md: '75%%' }}
        align={isCurrentUser ? 'flex-end' : 'flex-start'}
        direction="column"
      >
        <Stack direction="column" spacing="2px">
          {hideSeparator ? null : (
            <Stack
              direction="row"
              justifyContent={isCurrentUser ? 'flex-end' : 'start'}
              align="center"
              spacing="8px"
              mt="12px"
            >
              {isCurrentUser ? null : hasUserRole(user, 'admin:super') ? (
                <ShwLink
                  textDecor="none"
                  color="fg"
                  fontWeight="bold"
                  href={getAdminUrl(`user/${messageUserId}`)}
                >
                  {messageDisplayName}
                </ShwLink>
              ) : (
                <Text as="span" fontWeight="bold">
                  {messageDisplayName}
                </Text>
              )}
              <Text color="fg.muted" variant="supporting">
                <RelativeTime time={message.created_at} />
              </Text>
              {message.last_edited_at != null &&
              new Date(message.last_edited_at) > new Date(message.created_at) ? (
                <Text color="fg.muted" variant="small" fontWeight="bold">
                  Edited
                </Text>
              ) : null}
            </Stack>
          )}
          {hideSeparator &&
          message.last_edited_at != null &&
          new Date(message.last_edited_at) > new Date(message.created_at) ? (
            <Flex justify={isCurrentUser ? 'flex-end' : 'start'}>
              <Text color="fg.muted" variant="small" fontWeight="bold">
                Edited
              </Text>
            </Flex>
          ) : null}
          <Stack
            direction={isCurrentUser ? 'row-reverse' : 'row'}
            align="center"
            whiteSpace="pre-wrap"
            spacing="4px"
          >
            <Flex
              backgroundColor={
                message.id === EMPTY_MESSAGE_ID
                  ? 'blue.focusRing'
                  : isCurrentUser
                    ? 'blue.solid'
                    : isRemovedMessage
                      ? 'bg'
                      : 'bg.muted'
              }
              borderRadius={isCurrentUser ? '8px 8px 0 8px' : '8px 8px 8px 0'}
              wordBreak="break-word"
              p={2}
              border={isRemovedMessage ? '1px solid' : undefined}
              borderColor={isRemovedMessage ? 'border' : undefined}
              flex={1}
            >
              {isHiddenMessage ? (
                <RichText
                  content={getBodyForHiddenEntity('message', 'rich-text')}
                  styleProps={{ color: 'fg.subtle' }}
                />
              ) : isDeletedMessage ? (
                <Text as="i" color="fg.subtle">
                  This message has been deleted
                </Text>
              ) : (
                <Stack direction="column" spacing="16px">
                  <RichText content={messageBody} styleProps={richTextStyleProps} />
                  {message.attachment_urls_safe != null ? (
                    <FileUploadGallery
                      uploads={message.attachment_urls_safe.map(src => ({ src }))}
                      bgColor="bg"
                    />
                  ) : null}
                </Stack>
              )}
            </Flex>
            {inView ? (
              <Box width={menuItems.length === 0 ? '36px' : '72px'} flexShrink={0}>
                <Stack
                  visibility={optionsVisible ? 'visible' : 'hidden'}
                  _groupHover={{ visibility: 'visible' }}
                  direction={isCurrentUser ? 'row-reverse' : 'row'}
                  align="center"
                  spacing="0px"
                >
                  {menuItems.length > 0 ? (
                    <Menu
                      buttonProps={{
                        'aria-label': 'Message Actions',
                        icon: <Icon faIcon={faEllipsis} size="md" color="fg.muted" />,
                      }}
                      itemGroups={[menuItems]}
                      gutter={0}
                      onOpen={() => setOptionsVisible(true)}
                      onClose={() => setOptionsVisible(false)}
                    />
                  ) : null}
                  <ReactionButton
                    onReact={onToggleReaction}
                    buttonComponent={() => (
                      <IconButton
                        variant="icon"
                        aria-label="React to Message"
                        icon={<Icon size="md" faIcon={faSmilePlus} />}
                      />
                    )}
                    onOpen={() => setOptionsVisible(true)}
                    onClose={() => setOptionsVisible(false)}
                  />
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </Stack>
        {currentReactions.length === 0 ? null : (
          <Wrap mt="4px" justify={isCurrentUser ? 'flex-end' : 'flex-start'}>
            <ReactionWrapItems
              reactions={currentReactions}
              onToggleReaction={onToggleReaction}
              canUserReact
              showAll={showAllReactions}
              setShowAll={setShowAllReactions}
              startWithShowAllButton={isCurrentUser}
            />
          </Wrap>
        )}
      </Flex>
      {inView ? (
        <>
          {canEditMessage && activeModal === 'edit' ? (
            <ModalV3
              open
              size="lg"
              title="Edit Message"
              onClose={() => setActiveModal(null)}
              body={() => (
                <Form
                  name="AdminEditMessage"
                  blocks={editMessageFormInputs}
                  onSubmit={handleSubmitEditMessage}
                />
              )}
            />
          ) : null}
          {activeModal === 'delete' ? (
            <ModalV3
              open
              size="lg"
              title="Delete Message"
              onClose={() => setActiveModal(null)}
              rightFooterContent={() => (
                <Stack direction="row" spacing="4px" align="center">
                  <Button onClick={() => setActiveModal(null)} variant="outline">
                    Cancel
                  </Button>
                  <Button variant="danger" onClick={handleSubmitDeleteMessage}>
                    Delete
                  </Button>
                </Stack>
              )}
              body={() => (
                <Stack direction="column" spacing="16px">
                  <Text>Are you sure you want to delete this message?</Text>
                  <Box backgroundColor="bg.muted" p="2">
                    <RichText content={messageBody} styleProps={{ color: 'fg' }} />
                  </Box>
                </Stack>
              )}
            />
          ) : null}
          {activeModal === 'report' && messageUserId != null ? (
            <ReportModalV2
              open
              entity={{ type: 'message', id: message.id }}
              onClose={() => setActiveModal(null)}
              reportedUserId={messageUserId}
              reportedUserName={userDisplayName}
            />
          ) : null}
        </>
      ) : null}
    </Stack>
  )
}
